import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { App, Button, Popconfirm, Switch, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { deleteCategory, listNominees, reorderCategories, saveCategory, seed2026 } from '../api';
import CategoryDrawer, { MedalImg } from '../components/category-drawer';
import { GalaCategory, errorText, moveId } from '../contract';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
  categories: GalaCategory[];
  onChanged: () => void | Promise<void>;
}

/** Mã gợi ý cho hạng mục mới: số lớn nhất trong các mã số + 1, 2 chữ số. */
const nextCode = (categories: GalaCategory[]) => {
  const max = categories.reduce((m, c) => {
    const n = /^\d+$/.test(c.code) ? Number(c.code) : 0;
    return Math.max(m, n);
  }, 0);
  return String(max + 1).padStart(2, '0');
};

/** Tab Hạng mục: bảng, dời lên / xuống, sửa trong ngăn kéo, nạp 10 hạng mục 2026. */
const CategoriesTab = ({ eventId, categories, onChanged }: Props) => {
  const { message } = App.useApp();
  const [editing, setEditing] = useState<GalaCategory | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [seeding, setSeeding] = useState(false);
  // Số đề cử mỗi hạng mục, tải khi mở hộp xác nhận xoá (xoá hạng mục xoá luôn đề cử).
  const [nomineeCounts, setNomineeCounts] = useState<Record<number, number> | null>(null);

  const loadNomineeCounts = async () => {
    try {
      const res = await listNominees(eventId);
      setNomineeCounts(
        (res?.categories ?? []).reduce<Record<number, number>>((acc, c) => {
          acc[c.id] = c.nominees.length;
          return acc;
        }, {})
      );
    } catch {
      setNomineeCounts(null);
    }
  };

  const deleteText = (row: GalaCategory) => {
    const n = nomineeCounts?.[row.id];
    if (n === undefined) {
      return 'Xoá hẳn, không khôi phục được. Đề cử của hạng mục này (nếu có) cũng bị xoá.';
    }
    return n > 0
      ? `Xoá cả ${n} đề cử của hạng mục này. Xoá hẳn, không khôi phục được.`
      : 'Xoá hẳn, không khôi phục được.';
  };

  const openForm = (category: GalaCategory | null) => {
    setEditing(category);
    setFormOpen(true);
  };

  const run = async (id: number, task: () => Promise<unknown>, fail: string) => {
    setBusyId(id);
    try {
      await task();
      await onChanged();
    } catch (e) {
      message.error(errorText(e, fail));
    } finally {
      setBusyId(null);
    }
  };

  const move = (row: GalaCategory, delta: -1 | 1) =>
    run(
      row.id,
      () =>
        reorderCategories(
          eventId,
          moveId(
            categories.map((c) => c.id),
            row.id,
            delta
          )
        ),
      'Không đổi được thứ tự hạng mục.'
    );

  const toggleHidden = (row: GalaCategory, hidden: boolean) =>
    run(row.id, () => saveCategory(eventId, { isHidden: hidden }, row.id), 'Không đổi được ẩn / hiện.');

  const remove = (row: GalaCategory) =>
    run(
      row.id,
      async () => {
        await deleteCategory(eventId, row.id);
        message.success('Đã xoá hạng mục.');
      },
      'Không xoá được hạng mục.'
    );

  const onSeed = async () => {
    setSeeding(true);
    try {
      const res = await seed2026(eventId);
      message.success(
        res.categoriesAdded || res.blocksAdded
          ? `Đã nạp ${res.categoriesAdded} hạng mục và ${res.blocksAdded} khối nội dung.`
          : 'Đã có đủ 10 hạng mục, không nạp thêm.'
      );
      await onChanged();
    } catch (e) {
      message.error(errorText(e, 'Không nạp được hạng mục mẫu.'));
    } finally {
      setSeeding(false);
    }
  };

  const columns: ColumnsType<GalaCategory> = [
    { title: 'Mã', dataIndex: 'code', width: 70 },
    {
      title: 'Hạng mục',
      render: (_: unknown, row) => (
        <div className={styles.medalCell}>
          <MedalImg medal={row.medal} />
          <div className={styles.titleCell}>
            <strong>{row.nameVi}</strong>
            <span className={styles.subtle}>{row.nameEn}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Nhóm',
      dataIndex: 'groupLabel',
      width: 170,
      render: (v: string, row) => <Tag title={row.groupCode}>{v}</Tag>,
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantityLabel',
      width: 170,
      render: (v: string, row) => v || (row.quantityCount ?? '—'),
    },
    {
      title: 'Hiện',
      dataIndex: 'isHidden',
      width: 80,
      render: (hidden: boolean, row) => (
        <Switch
          checked={!hidden}
          size="small"
          disabled={busyId !== null}
          aria-label={`Hiện hạng mục ${row.code} trong app`}
          onChange={(on) => toggleHidden(row, !on)}
        />
      ),
    },
    {
      title: '',
      width: 220,
      render: (_: unknown, row, index) => (
        <div className={styles.headExtra}>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            aria-label="Dời hạng mục lên"
            disabled={index === 0 || busyId !== null}
            onClick={() => move(row, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            aria-label="Dời hạng mục xuống"
            disabled={index === categories.length - 1 || busyId !== null}
            onClick={() => move(row, 1)}
          />
          <Button size="small" icon={<EditOutlined />} onClick={() => openForm(row)}>
            Sửa
          </Button>
          <Popconfirm
            title={`Xoá hạng mục ${row.code}?`}
            description={deleteText(row)}
            onOpenChange={(open) => {
              if (open) {
                setNomineeCounts(null);
                loadNomineeCounts();
              }
            }}
            okText="Xoá"
            okButtonProps={{ danger: true }}
            cancelText="Không"
            onConfirm={() => remove(row)}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label="Xoá hạng mục"
              disabled={busyId !== null}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className={styles.toolbar}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          Thêm hạng mục
        </Button>
        <Popconfirm
          title="Nạp 10 hạng mục 2026?"
          description="Chỉ thêm hạng mục có mã chưa có (không ghi đè). Khối nội dung chỉ được nạp khi sự kiện chưa có khối nào."
          okText="Nạp"
          cancelText="Không"
          onConfirm={onSeed}
        >
          <Button icon={<ThunderboltOutlined />} loading={seeding}>
            Nạp 10 hạng mục 2026
          </Button>
        </Popconfirm>
      </div>

      <Table<GalaCategory>
        rowKey="id"
        columns={columns}
        dataSource={categories}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Chưa có hạng mục nào. Bấm "Nạp 10 hạng mục 2026" hoặc Thêm hạng mục.' }}
      />

      <CategoryDrawer
        open={formOpen}
        eventId={eventId}
        category={editing}
        suggestCode={nextCode(categories)}
        onClose={() => setFormOpen(false)}
        onSaved={onChanged}
      />
    </div>
  );
};

export default CategoriesTab;
